/**
 * "Start sourcing" (Sofia, Oct 18 2026): one click, and the FINN Outreach Sender reads the job's
 * Recruiter project list page by page in the next tab; each page goes to the proxy, which screens
 * it against the brief and puts the fits on the shortlist. The loop lives here, one page at a time:
 * read → screen → next, until the job says done (target, cap, end of list), the recruiter stops it,
 * or LinkedIn shows anything unexpected — then it stops and leaves that tab open to be looked at.
 *
 * Second stage (Sofia, Oct 18 2026): every FIT from a page gets its full Recruiter profile read
 * (one profile per fit, never per list entry) and the full profile check against the brief, so the
 * recruiter decides on the whole CV, not on the three positions of a list card. Misfits are not
 * opened.
 *
 * Stage 2 (Theresa, Oct 20 2026): a job with keywords first has the extension run that search in the
 * project; the address LinkedIn gives it is saved on the job and read like any list. A job that
 * already ran its search reads the saved address again.
 *
 * "Not read yet" (Sofia, Oct 23 2026): a stopped run leaves fits whose profile was never read. They
 * wait off the shortlist; `readPeople` reads and checks them with the same loop (`readFits`),
 * outside any job — its status carries `jobId: READ_ID`.
 *
 * Closing the laptop mid-run (Sofia, Nov 2 2026): a step that fails because the machine slept or the
 * connection dropped (`wakeDetector.ts`) is tried again — the SAME step, once the browser is back
 * online — up to 3 times, then the run stops as before. LinkedIn's own refusals (login, warning, left
 * LinkedIn) still stop at once. Nothing closes or re-creates the reading window on a retry, and the
 * People Space window keeps its size: `sourceDone` runs only at the very end.
 */
import { useRef, useState } from 'react';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate, SourcingJob } from '../../types/taOutreach';
import { useWakeDetector, type WakeDetector } from './wakeDetector';
import { explainCapture, explainList, explainSearch, sourceCaptureInExtension, sourceDone, sourcePageInExtension, sourceSaveInExtension, sourceSearchInExtension, type ListResult } from './outreachExtension';

const PAGE = 25;
/** The status/running id of a `readPeople` run (no job behind it). */
export const READ_ID = 'read-profiles';

type Say = (text: string, problem?: boolean, etaMs?: number) => void;

/** The wait before each retry of a step, once back online (Nov 2 2026); its length = the number of retries. */
export const RESUME_RETRY_MS = [5_000, 15_000, 45_000];
/**
 * A screen call that broke off can still finish on the proxy (a page takes up to ~2 minutes to screen),
 * so it is sent again only once the job shows it did not, or this long after it was first sent.
 */
export const SCREEN_GRACE_MS = 150_000;
const SCREEN_POLL_MS = 15_000;
/** A wake or a drop just before a step started still counts: the network comes back in steps. */
const RECENT_MS = 30_000;
/** LinkedIn said no: never retried, the run stops exactly as before. */
const REFUSALS = new Set(['not_logged_in', 'linkedin_warning', 'left_linkedin', 'not_a_project_list', 'not_a_project_list_url', 'unknown_type', 'not_a_profile_url', 'not_a_search_page_url', 'no_keywords']);
export const ASLEEP_TEXT = 'Laptop was asleep — continuing…';
export const OFFLINE_TEXT = 'Connection lost — continuing once it is back…';

const delay = (ms: number) => (ms > 0 ? new Promise<void>((r) => setTimeout(r, ms)) : Promise.resolve());
type ExtResult = { ok: boolean; error?: string };

export function useSourcingRun({
  onJob,
  onItems,
  wake: givenWake,
  timing = {},
}: {
  onJob: (job: SourcingJob) => void;
  onItems: (items: OutreachCandidate[]) => void;
  /** Tests pass their own. */
  wake?: WakeDetector;
  /** Tests shorten the waits. */
  timing?: { retryMs?: number[]; screenGraceMs?: number; pollMs?: number };
}) {
  const wake = useWakeDetector(givenWake);
  const retryMs = timing.retryMs ?? RESUME_RETRY_MS;
  const screenGraceMs = timing.screenGraceMs ?? SCREEN_GRACE_MS;
  const pollMs = timing.pollMs ?? SCREEN_POLL_MS;
  const [runningId, setRunningId] = useState<string | null>(null);
  /**
   * `since` lets the row show how long the current step has been going (the run looked stuck while
   * a 2-minute check ran, Oct 19 2026); `etaMs` is the estimate for the profile reads still to do,
   * from the reads already timed in this run (25 s each until one is measured).
   */
  const [status, setStatus] = useState<{ jobId: string; text: string; problem: boolean; since: number; etaMs?: number } | null>(null);
  const stop = useRef(false);

  /** Slept or offline since shortly before `t0`. */
  function disturbed(t0: number) {
    wake.check();
    return wake.disturbedSince(t0 - RECENT_MS);
  }
  /** An extension answer worth trying again: not a refusal, and the machine slept or went offline meanwhile. */
  const extTransient = (r: ExtResult, t0: number) => !r.ok && !REFUSALS.has(r.error || '') && disturbed(t0);
  /** A proxy call worth trying again: no answer at all (network), or a 5xx around a sleep. A 4xx never. */
  function apiTransient(err: unknown, t0: number) {
    const status = (err as { status?: unknown } | null)?.status;
    if (typeof status === 'number') return status >= 500 && disturbed(t0);
    const what = `${(err as { code?: string } | null)?.code || ''} ${err instanceof Error ? err.message : ''}`;
    // Not every TypeError: a coding fault is one too, and must show at once (CodeRabbit, PR #783).
    return /network|failed to fetch|load failed/i.test(what) || disturbed(t0);
  }
  /** Retry `n` of a failed step: say why, wait until online and a moment more. false = give up. */
  async function backOff(n: number, t0: number, say: Say) {
    if (n >= retryMs.length || stop.current) return false;
    say(wake.sleptSince(t0 - RECENT_MS) ? ASLEEP_TEXT : OFFLINE_TEXT);
    await wake.waitUntilOnline();
    // Stop (or leaving the page) releases the wait: no further delay then.
    if (stop.current) return false;
    await delay(retryMs[n]);
    return !stop.current;
  }
  /** One extension step, tried again after a sleep or a drop. The last answer is returned as it came. */
  async function viaExt<T extends ExtResult>(run: () => Promise<T>, say: Say): Promise<T> {
    for (let n = 0; ; n += 1) {
      const t0 = Date.now();
      const r = await run();
      if (r.ok || !extTransient(r, t0) || !(await backOff(n, t0, say))) return r;
    }
  }
  /** One proxy call, tried again after a network failure. The last error is thrown as it came. */
  async function viaNet<T>(run: () => Promise<T>, say: Say): Promise<T> {
    for (let n = 0; ; n += 1) {
      const t0 = Date.now();
      try {
        return await run();
      } catch (err) {
        if (!apiTransient(err, t0) || !(await backOff(n, t0, say))) throw err;
      }
    }
  }
  /** The job as the proxy has it now, or null. */
  const currentJob = (job: SourcingJob) =>
    api
      .fetchSourcingJobs(job.postingId)
      .then((r) => r.jobs.find((j) => j.id === job.id) || null)
      .catch(() => null);
  /**
   * Screening a page is not idempotent (its counts would add up twice, and its fits come back as
   * "known" without their profile read), so a broken-off call is NOT simply sent again: first the job
   * is read — the proxy goes on screening when the connection drops. Its `nextStart` past this page
   * means the page went through; it is then skipped (`through`), its fits wait under "Not read yet".
   */
  async function screen(job: SourcingJob, at: number, cards: NonNullable<ListResult['cards']>, say: Say): Promise<{ job: SourcingJob; items: OutreachCandidate[]; through?: boolean }> {
    for (let n = 0; ; n += 1) {
      const t0 = Date.now();
      try {
        return await api.screenSourcingPage(job.id, at, cards);
      } catch (err) {
        if (!apiTransient(err, t0) || !(await backOff(n, t0, say))) throw err;
        for (;;) {
          const cur = await currentJob(job);
          if (cur && (cur.progress.nextStart || 0) > at) return { job: cur, items: [], through: true };
          if (stop.current || Date.now() - t0 >= screenGraceMs) break;
          await delay(pollMs);
        }
        if (stop.current) throw err;
      }
    }
  }

  /**
   * Read each person's full Recruiter profile and check it against the brief, one after the other.
   * Someone not read stays with the list evidence. `halted` when LinkedIn stopped the reading (login,
   * left LinkedIn) — the caller stops too. `checked` / `failed` say who actually got through
   * (CodeRabbit, PR #713: a summary must not claim reads that failed).
   */
  async function readFits(fits: OutreachCandidate[], say: Say, readMs: number[]): Promise<{ halted: boolean; checked: number; skipped: number; failed: string[] }> {
    let checked = 0;
    // Read, but already reached for this role: no check runs (CodeRabbit, PR #713).
    let skipped = 0;
    const failed: string[] = [];
    const perRead = () => (readMs.length ? readMs.reduce((a, b) => a + b, 0) / readMs.length : 25_000);
    for (const [i, c] of fits.entries()) {
      if (stop.current) break;
      const startedAt = Date.now();
      say(`Reading the full profile of ${c.name} (${i + 1} of ${fits.length})…`, false, (fits.length - i) * perRead());
      const full = await viaExt(() => sourceCaptureInExtension(c.recruiterId as string), say);
      if (!full.ok || !full.url || !full.text) {
        if (full.error === 'not_logged_in' || full.error === 'left_linkedin') {
          say(explainCapture(full), true);
          return { halted: true, checked, skipped, failed };
        }
        console.warn('[sourcing] full profile not read', c.recruiterId, full.error, full.detail || '');
        say(`${c.name}: full profile not read (${full.error || 'unknown'}) — kept with the list evidence.`, true);
        failed.push(c.name);
        continue; // this one stays with its list evidence, under "Not read yet"
      }
      try {
        const capture = { url: full.url, text: full.text, name: full.name, headline: full.headline, location: full.location, publicUrl: full.publicUrl, expanded: full.expanded, markersLeft: full.markersLeft };
        const saved = await viaNet(() => api.saveCapture(c.id, capture), say);
        onItems([saved.item]);
        // The full profile showed them as already reached for this role: no profile check needed.
        if (saved.item.sourcing?.skipReason) {
          skipped += 1;
          continue;
        }
        // The time already spent on this profile is not counted twice (CodeRabbit, PR #629).
        say(`Checking ${c.name}’s full profile against the brief…`, false, Math.max(0, (fits.length - i) * perRead() - (Date.now() - startedAt)));
        onItems([(await viaNet(() => api.analyseOutreach(c.id), say)).item]);
        readMs.push(Date.now() - startedAt);
        checked += 1;
      } catch (err) {
        // The list evidence stays; nothing else depends on this one — but say why.
        console.warn('[sourcing] full profile not saved', c.recruiterId, err);
        say(`${c.name}: ${err instanceof Error ? err.message : 'full profile not saved'} — kept with the list evidence.`, true);
        failed.push(c.name);
      }
    }
    return { halted: false, checked, skipped, failed };
  }

  async function start(job: SourcingJob) {
    // A keyword job has no address until its search ran (Oct 22 2026: no project needed).
    if ((!job.sourceUrl && !job.search) || runningId) return;
    stop.current = false;
    setRunningId(job.id);
    let at = job.progress.nextStart || 0;
    // The cap counts from where this job started (a job on a list read before starts further in).
    const cap = (job.progress.startedFrom || 0) + (job.maxScreened || 250);
    let keepTab = false;
    let halted = false;
    let pages = 0;
    const readMs: number[] = [];
    const say: Say = (text, problem = false, etaMs) => setStatus({ jobId: job.id, text, problem, since: Date.now(), etaMs });
    let sourceUrl = job.sourceUrl || '';
    try {
      if (job.search && !job.search.url) {
        say(`Running the search from the brief ${job.search.projectId ? 'in your Recruiter project' : 'across LinkedIn Recruiter'}, in the LinkedIn window on the right…`);
        const keywords = job.search.keywords;
        const projectId = job.search.projectId;
        const searched = await viaExt(() => sourceSearchInExtension(keywords, projectId), say);
        if (!searched.ok || !searched.url) {
          keepTab = searched.error !== 'unknown_type';
          say(explainSearch(searched), true);
          return;
        }
        const url = searched.url;
        const saved = await viaNet(() => api.setJobSearch(job.id, url, searched.filters || []), say);
        onJob(saved.job);
        sourceUrl = saved.job.sourceUrl || sourceUrl;
      }
      while (!stop.current && at < cap) {
        const pageNo = Math.floor(at / PAGE) + 1;
        const pagesMax = Math.ceil(cap / PAGE);
        say(`Page ${pageNo} of at most ${pagesMax}: reading candidates ${at + 1}–${at + PAGE} in the LinkedIn window on the right…`);
        // The same `start=` again after a sleep: the reading window is reused, never re-created.
        const pageUrl = `${sourceUrl}${sourceUrl.includes('?') ? '&' : '?'}start=${at}`;
        const read = await viaExt(() => sourcePageInExtension(pageUrl), say);
        if (!read.ok) {
          // A warning or a login page: leave the tab where it is, so the recruiter can see it.
          keepTab = read.error === 'linkedin_warning' || read.error === 'not_logged_in';
          say(explainList(read), true);
          break;
        }
        const cards = read.cards || [];
        // A short page that is not the end of the list is worth knowing about (quality check, Oct 18).
        if (cards.length > 0 && cards.length < PAGE && read.debug) console.info('[sourcing] short page', at, read.debug);
        say(`Page ${pageNo} of at most ${pagesMax}: the agent is checking these ${cards.length} candidates against the brief — the LinkedIn window waits meanwhile, that is normal.`);
        const r = await screen(job, at, cards, say);
        if (r.through) console.info('[sourcing] page screened while the connection was gone', at);
        pages += 1;
        onJob(r.job);
        if (r.items.length) onItems(r.items);
        const fits = r.items.filter((c) => !c.sourcing?.skipReason && c.recruiterId);
        // Into the role's LinkedIn project right away (Theresa, Oct 22 2026: at the latest when suggested),
        // while this project search page is still open. Not a stop if it fails: the run goes on.
        if (fits.length && /\/talent\/hire\/\d+\/discover\//.test(sourceUrl)) {
          say(`Saving ${fits.length} to the project’s LinkedIn pipeline…`);
          const saved = await sourceSaveInExtension(fits.map((c) => c.recruiterId as string));
          if (saved.ok && saved.results?.length) {
            try {
              const rec = await api.recordPipelineSaves(job.id, saved.results);
              if (rec.items.length) onItems(rec.items);
            } catch (err) {
              console.warn('[sourcing] pipeline saves not recorded', err);
            }
          } else if (!saved.ok) {
            console.warn('[sourcing] pipeline save skipped', saved.error);
          }
        }
        halted = (await readFits(fits, say, readMs)).halted;
        if (stop.current || halted) break;
        if (r.job.status !== 'running') {
          say(r.job.progress.note || 'Done.');
          break;
        }
        at += PAGE;
      }
      if (stop.current) say('Stopped by you. Start again to continue where it stopped.');
    } catch (err) {
      say(err instanceof Error ? err.message : 'Sourcing stopped.', true);
    } finally {
      if (!keepTab) await sourceDone();
      setRunningId(null);
      void logAnalyticsEvent('ta_outreach_sourcing_run_finished', { pages, stopped_by_user: stop.current });
    }
  }

  /** Read and check people a stopped run left unread ("Not read yet", Oct 23 2026). */
  async function readPeople(people: OutreachCandidate[]) {
    const todo = people.filter((c) => c.recruiterId && !c.gap);
    if (!todo.length || runningId) return;
    stop.current = false;
    setRunningId(READ_ID);
    const say: Say = (text, problem = false, etaMs) => setStatus({ jobId: READ_ID, text, problem, since: Date.now(), etaMs });
    try {
      const r = await readFits(todo, say, []);
      const done = `${r.checked} of ${todo.length} profile${todo.length === 1 ? '' : 's'} read and checked`;
      const reached = r.skipped ? ` ${r.skipped} already reached for this role — moved to “Not a fit”.` : '';
      const notRead = r.failed.length ? ` Not read: ${r.failed.join(', ')} — they stay under “Not read yet”.` : '';
      if (stop.current) say(`Stopped by you: ${done}.${reached}${notRead}`);
      else if (!r.halted) say(`Done: ${done}.${reached}${notRead}`, r.failed.length > 0);
    } catch (err) {
      say(err instanceof Error ? err.message : 'Reading stopped.', true);
    } finally {
      await sourceDone();
      setRunningId(null);
      void logAnalyticsEvent('ta_outreach_unread_profiles_read', { count: todo.length, stopped_by_user: stop.current });
    }
  }

  // Immediate feedback: the run only stops between two reads (a profile can take 20 s), and a
  // silent button looked broken (Oct 18 2026).
  const stopNow = () => {
    stop.current = true;
    // A run waiting offline ends now; its finally (sourceDone) runs (CodeRabbit, PR #783).
    wake.cancelWaits();
    setStatus((s) => (s ? { ...s, text: 'Stopping after the current read… (reload the page to stop at once)', problem: false } : s));
  };
  return { runningId, status, start, readPeople, stop: stopNow, stopping: () => stop.current };
}
